'use client'

import * as React from 'react'
import { Button, type ButtonProps } from './button'
import { Spinner } from './spinner'
import { cn } from '../../lib/utils'

export interface LoadingButtonProps extends ButtonProps {
  /** Whether the button is in a loading state */
  loading?: boolean
  /** Text to show while loading (defaults to children) */
  loadingText?: React.ReactNode
  /** Position of the spinner relative to text */
  spinnerPosition?: 'start' | 'end'
  /** Custom spinner className */
  spinnerClassName?: string
}

/**
 * LoadingButton - Button with integrated loading state and spinner
 *
 * Features:
 * - Shows spinner when loading
 * - Optionally changes text while loading
 * - Disables button while loading
 * - Supports RTL layout
 *
 * @example
 * // Basic usage
 * <LoadingButton loading={isSubmitting}>Save</LoadingButton>
 *
 * @example
 * // With loading text
 * <LoadingButton loading={isSubmitting} loadingText="Saving...">
 *   Save
 * </LoadingButton>
 *
 * @example
 * // Spinner at end
 * <LoadingButton loading={isSubmitting} spinnerPosition="end">
 *   Save
 * </LoadingButton>
 */
const LoadingButton = React.forwardRef<HTMLButtonElement, LoadingButtonProps>(
  (
    {
      children,
      loading = false,
      loadingText,
      spinnerPosition = 'start',
      spinnerClassName,
      disabled,
      className,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading

    // Determine content to show
    const content = loading && loadingText ? loadingText : children

    return (
      <Button
        ref={ref}
        disabled={isDisabled}
        className={cn(
          'relative',
          loading && 'cursor-wait',
          className
        )}
        {...props}
      >
        {/* Spinner at start */}
        {loading && spinnerPosition === 'start' && (
          <Spinner className={cn('me-2 h-4 w-4', spinnerClassName)} />
        )}

        {/* Content */}
        <span className={cn(loading && 'inline-flex items-center')}>
          {content}
        </span>

        {/* Spinner at end */}
        {loading && spinnerPosition === 'end' && (
          <Spinner className={cn('ms-2 h-4 w-4', spinnerClassName)} />
        )}
      </Button>
    )
  }
)
LoadingButton.displayName = 'LoadingButton'

export { LoadingButton }
